import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    Phone, Mail, MessageSquare, Users, Gavel,
    ArrowRightCircle, XCircle, ChevronDown, ChevronRight,
    Clock, CheckCircle2, User
} from 'lucide-react';
import { ClientEvent, EventType, EVENT_TYPE_LABELS } from '../../types/client-activity.types';
import { activityService } from '../../lib/activityService';
import { Button } from '../ui/button';
import { AddEventForm } from './AddEventForm';
import { cn } from '../../lib/cn';
import { Card } from '../ui/card';

interface ActivityTimelineProps {
    clientId: string;
}

const EVENT_ICONS: Record<EventType, React.ElementType> = {
    call: Phone,
    email: Mail,
    messaggio: MessageSquare,
    meeting: Users,
    decisione: Gavel,
    follow_up: ArrowRightCircle,
    chiuso: XCircle,
};

const EVENT_COLORS: Record<EventType, string> = {
    call: 'bg-blue-100 text-blue-600',
    email: 'bg-yellow-100 text-yellow-600',
    messaggio: 'bg-green-100 text-green-600',
    meeting: 'bg-purple-100 text-purple-600',
    decisione: 'bg-red-100 text-red-600',
    follow_up: 'bg-orange-100 text-orange-600',
    chiuso: 'bg-gray-100 text-gray-600',
};

import { ConfirmDeleteDialog } from '../ui/confirm-delete-dialog';
import { Trash2 } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { useAuth } from '../../hooks/useAuth';
import { formatEventAuthor } from '../../lib/eventAuthor';

export function ActivityTimeline({ clientId }: ActivityTimelineProps) {
    const { profile } = useAuth();
    const isAdmin = profile?.role === 'admin';
    const [events, setEvents] = useState<ClientEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
    const [eventToDelete, setEventToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { addNotification } = useStore();

    const loadEvents = async () => {
        setLoading(true);
        try {
            const data = await activityService.getEvents(clientId);
            setEvents(data);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEvents();
    }, [clientId]);

    const toggleExpand = (id: string) => {
        setExpandedEventId(expandedEventId === id ? null : id);
    };

    const handleDeleteEvent = async () => {
        if (!eventToDelete) return;

        setIsDeleting(true);
        try {
            await activityService.deleteEvent(eventToDelete);
            addNotification({ type: 'success', title: 'Attività eliminata' });
            await loadEvents();
        } catch (error) {
            console.error('Error deleting event:', error);
            addNotification({
                type: 'error',
                title: 'Errore durante eliminazione',
                message: error instanceof Error ? error.message : undefined,
            });
        } finally {
            setIsDeleting(false);
            setEventToDelete(null);
        }
    };

    return (
        <div className="space-y-4">
            <ConfirmDeleteDialog
                open={!!eventToDelete}
                onOpenChange={(open) => !open && setEventToDelete(null)}
                onConfirm={handleDeleteEvent}
                isDeleting={isDeleting}
                title="Elimina Attività"
                description="Sei sicuro di voler eliminare questa attività? L'azione è irreversibile."
            />

            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Timeline Attività</h2>
                <Button onClick={() => setIsAdding(!isAdding)} variant={isAdding ? "secondary" : "default"}>
                    {isAdding ? 'Annulla' : '+ Nuova Attività'}
                </Button>
            </div>

            {isAdding && (
                <AddEventForm
                    clientId={clientId}
                    onEventAdded={() => {
                        setIsAdding(false);
                        loadEvents();
                    }}
                    onCancel={() => setIsAdding(false)}
                />
            )}

            <div className="relative space-y-4">
                {/* Vertical Line */}
                <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-gray-200 -z-10" />

                {loading ? (
                    <div className="text-center py-4 text-gray-500">Caricamento attività...</div>
                ) : events.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                        Nessuna attività registrata. Inizia aggiungendone una!
                    </div>
                ) : (
                    events.map((event) => {
                        const Icon = EVENT_ICONS[event.event_type] || Clock;
                        const isExpanded = expandedEventId === event.id;

                        return (
                            <div key={event.id} className="relative pl-14">
                                {/* Icon Badge */}
                                <div className={cn(
                                    "absolute left-2 top-0 w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10",
                                    EVENT_COLORS[event.event_type]
                                )}>
                                    <Icon className="w-4 h-4" />
                                </div>

                                <Card
                                    className={cn(
                                        "p-3 hover:shadow-md transition-shadow cursor-pointer border-l-4 group relative", // Added group and relative
                                        event.status === 'done' ? "border-l-green-500" : "border-l-transparent"
                                    )}
                                    onClick={() => toggleExpand(event.id)}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                <span className="text-xs font-bold uppercase text-gray-500 tracking-wider">
                                                    {EVENT_TYPE_LABELS[event.event_type]}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {format(new Date(event.event_date), "d MMM yyyy, HH:mm", { locale: it })}
                                                </span>
                                                <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    {formatEventAuthor(event.creator)}
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-semibold text-gray-900 mt-1 break-words">{event.title}</h4>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Delete Button - solo admin, visibile all'hover */}
                                            {isAdmin && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEventToDelete(event.id);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}

                                            {event.notes && (
                                                <div className="text-gray-400">
                                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Content: Notes & Reminders */}
                                    {isExpanded && event.notes && (
                                        <div className="mt-3 pt-3 border-t text-sm text-gray-600 whitespace-pre-wrap">
                                            {event.notes}
                                        </div>
                                    )}

                                    {/* Inline Reminders (Read-only view for now) */}
                                    {event.reminders && event.reminders.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {event.reminders.map(rem => (
                                                <div key={rem.id} className={cn(
                                                    "text-xs px-2 py-0.5 rounded-full flex items-center gap-1 border",
                                                    rem.is_completed ? "bg-green-50 text-green-700 border-green-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"
                                                )}>
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(rem.remind_at), "d MMM, HH:mm", { locale: it })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
